import { getData, postData, updateData, updateDataImg } from "@/request/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";

export const useGetCurrentUser = () => {
  return useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const response = await getData("user/profile");
      return response.data;
    },
    retry: 1,
    refetchOnWindowFocus: false, // Disable refetch on window focus
    staleTime: 1000 * 60 * 60 * 24, // 24 Hours
    gcTime: 1000 * 60 * 60 * 24, // 24 Hours
  });
};

export const useSignup = () => {
  // Signup logic here
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData("auth/signup", data);
      return response.data;
    },
  });
};

export const useVerifyEmail = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData("auth/verify-email", data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Email verified successfully!");
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Failed to verify email.");
    },
  });
};

export const useResendVerificationEmail = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData("auth/resend-verification-email", data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Verification email resent successfully!");
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message || "Failed to resend verification email.",
      );
    },
  });
};
export const useLogin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData("auth/login", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["current-user"] });
    },
  });
};

export const useUpdateUserPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await updateDataImg("user/update-photo", data);
      return response.data;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["current-user"] });
      toast.success(res?.message || "User photo updated successfully!");
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message || "Failed to update user photo.",
      );
    },
  });
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await updateData("user/update-profile", data);
      return response.data;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["current-user"] });
      toast.success(res?.message || "User profile updated successfully!");
    },
  });
};
export const useUpdateUserPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await updateData("user/update-password", data);
      return response.data;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["current-user"] });
      toast.success(res?.message || "User password updated successfully!");
    },
  });
};

export const useLogoutUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData("auth/logout", data);
      return response.data;
    },
    onSuccess: (res) => {
      queryClient.setQueryData(["current-user"], null);
      toast.success(res?.message || "User logged out successfully!");
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Failed to log out user.");
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData("auth/reset-password", data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Password reset successfully!");
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Failed to reset password.");
    },
  });
};
